import React, { useEffect, useState, useCallback } from 'react';
import PageShell, { SectionTitle } from './PageShell';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { MessagesChat } from '../components/MessagesChat';
import { Loader2, Users, MessageCircle, Search, X } from 'lucide-react';

const norm = (v) => (v || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

function fmtShort(iso) {
  try { return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }); } catch { return ''; }
}

export default function TeacherMessagesPage() {
  const { user, loading: authLoading } = useAuth();
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [search, setSearch] = useState('');

  const refresh = useCallback(async () => {
    try {
      const d = await api.messagesThreads();
      setThreads(d.threads || []);
      setActiveId((cur) => cur || d.threads?.[0]?.other?.id || null);
    } catch (_) { /* silent */ }
    setLoading(false);
  }, []);

  useEffect(() => { if (!authLoading && user?.role === 'enseignant') refresh(); }, [authLoading, user, refresh]);

  if (authLoading) return <PageShell><div className="max-w-6xl mx-auto p-12 text-white/50">Chargement…</div></PageShell>;
  if (!user || user.role !== 'enseignant') return <PageShell><div className="max-w-3xl mx-auto p-12 text-center text-white/60">Réservé aux enseignants ECSR.</div></PageShell>;

  const active = threads.find((t) => t.other.id === activeId);
  const q = norm(search.trim());
  const visibleThreads = q ? threads.filter((t) => norm(t.other.name).includes(q) || norm(t.other.email).includes(q)) : threads;

  return (
    <PageShell>
      <section className="max-w-7xl mx-auto px-4 lg:px-6 mt-8" data-testid="teacher-messages-page">
        <SectionTitle kicker="Enseignant · Messagerie" title="Boîte mail élèves" sub="Échange directement avec tes élèves rattachés — ils te répondent depuis leur espace." />

        {loading ? (
          <div className="flex items-center gap-2 text-white/50 py-16 justify-center"><Loader2 size={16} className="animate-spin" /> Chargement…</div>
        ) : threads.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-12 text-center" data-testid="teacher-messages-empty">
            <Users size={28} className="mx-auto text-white/25 mb-3" />
            <div className="text-white/70 text-[14.5px] font-semibold">Aucun élève rattaché</div>
            <p className="text-white/40 text-[12.5px] mt-2 max-w-md mx-auto">Partage ton code de rattachement depuis le tableau de bord — tes élèves apparaîtront ici et vous pourrez échanger.</p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-[280px_1fr] gap-4">
            {/* Liste des élèves */}
            <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] overflow-hidden self-start">
              {/* 🔎 Recherche d'élève dans la boîte mail */}
              <div className="relative p-2.5 border-b border-white/10">
                <Search size={13} className="absolute left-6 top-1/2 -translate-y-1/2 text-white/35 pointer-events-none" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Rechercher un élève…"
                  data-testid="messages-search-input"
                  className="w-full h-9 rounded-full bg-white/[0.04] border border-white/12 focus:border-red-500/60 outline-none pl-9 pr-8 text-white text-[12.5px]"
                />
                {search && (
                  <button onClick={() => setSearch('')} data-testid="messages-search-clear" aria-label="Effacer" className="absolute right-5 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-white/10 text-white/60 hover:text-white flex items-center justify-center">
                    <X size={11} />
                  </button>
                )}
              </div>
              {visibleThreads.length === 0 && (
                <div className="px-4 py-6 text-center text-white/40 text-[12.5px]" data-testid="messages-search-empty">Aucun élève pour « {search.trim()} »</div>
              )}
              {visibleThreads.map((t) => {
                const isActive = t.other.id === activeId;
                return (
                  <button
                    key={t.other.id}
                    onClick={() => { setActiveId(t.other.id); setThreads((prev) => prev.map((x) => x.other.id === t.other.id ? { ...x, unread: 0 } : x)); }}
                    data-testid={`thread-btn-${t.other.id}`}
                    className={`w-full flex items-center gap-3 px-4 py-3.5 text-left border-b border-white/5 transition ${isActive ? 'bg-red-600/15' : 'hover:bg-white/[0.03]'}`}
                  >
                    <span className="w-9 h-9 shrink-0 rounded-full bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center text-white text-[13px] font-bold">{(t.other.name || '?').charAt(0).toUpperCase()}</span>
                    <span className="min-w-0 flex-1">
                      <span className={`block text-[13.5px] truncate ${t.unread > 0 ? 'text-white font-bold' : 'text-white/80'}`}>{t.other.name}</span>
                      <span className="block text-[11.5px] text-white/40 truncate">{t.last_message ? t.last_message.body : 'Aucun message'}</span>
                    </span>
                    <span className="flex flex-col items-end gap-1 shrink-0">
                      {t.last_message && <span className="text-[10px] text-white/35 font-mono-spec">{fmtShort(t.last_message.created_at)}</span>}
                      {t.unread > 0 && <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold inline-flex items-center justify-center">{t.unread}</span>}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Conversation */}
            <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] overflow-hidden flex flex-col">
              {active ? (
                <>
                  <div className="px-4 h-12 border-b border-white/10 flex items-center gap-2.5">
                    <MessageCircle size={15} className="text-red-400" />
                    <span className="text-white text-[14px] font-semibold">{active.other.name}</span>
                    <span className="text-white/35 text-[11.5px]">{active.other.email}</span>
                  </div>
                  <MessagesChat otherId={active.other.id} otherName={active.other.name?.split(' ')[0] || 'ton élève'} />
                </>
              ) : (
                <div className="p-12 text-center text-white/40 text-[13px]">Sélectionne un élève pour ouvrir la conversation.</div>
              )}
            </div>
          </div>
        )}
      </section>
    </PageShell>
  );
}
